import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  selectAllSuperlatives,
  selectCurrentSuperlativeIndex,
  getSuperlativesAction,
} from "../superlativeSlice";
import { selectGameId } from "../../gamestate/gameStateSlice";
import GameboardLeaderboard from "../../gameboard/components/GameBoardLeaderboard";
import SpotlightCard from "./SpotlightCard";
import ProgressDots from "./ProgressDots";
import Provider from "provider";
import closingThemeMp3 from "sounds/closing-theme.mp3";

const HOLD_MS = 8000;
const EXIT_MS = 400;

const Root = styled(Box)({
  width: "100%",
  height: "100%",
  background: "linear-gradient(135deg, #faf2d2 0%, #eeeeee 50%, #e1f5f3 100%)",
  position: "fixed",
  top: 0,
  left: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

// Pinned to the top-left so it doesn't push the centered card off-center.
const LeaderboardOverlay = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: theme.spacing(3),
  left: theme.spacing(3),
  width: 360,
  zIndex: 1,
}));

const RevealCol = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  padding: "0 24px",
});

// Stack lets the incoming and outgoing cards occupy the same grid cell so they
// can cross each other during a transition.
const CardStack = styled(Box)({
  display: "grid",
  width: "100%",
  maxWidth: 720,
  overflow: "hidden",
  "& > *": {
    gridArea: "1 / 1",
  },
});

const BigScreenReveal = () => {
  const dispatch = useDispatch();
  const gameId = useSelector(selectGameId);
  const superlatives = useSelector(selectAllSuperlatives);
  const remoteIndex = useSelector(selectCurrentSuperlativeIndex);

  // localIndex drives the rendered "incoming" card. Synced from remoteIndex so
  // outside resets (e.g., a controller advancing manually) propagate, but the
  // local timer can advance it synchronously alongside setting the outgoing
  // card so both state updates render in the same React batch.
  const [localIndex, setLocalIndex] = useState(remoteIndex);
  const [outgoingSuperlative, setOutgoingSuperlative] = useState(null);
  const tickerRef = useRef(null);
  const outgoingCleanupRef = useRef(null);

  useEffect(() => {
    if (gameId) dispatch(getSuperlativesAction(gameId));
  }, [gameId, dispatch]);

  // Restore the closing theme on the big-screen reveal (was lost when the
  // deprecated SuperlativesShow component was removed in 7ec0920). Plays
  // once, full volume, like the original.
  useEffect(() => {
    const audio = new Audio(closingThemeMp3);
    audio.play().catch(() => {
      // Autoplay may be blocked if the user hasn't interacted with the page
      // yet — failing silently is preferable to throwing.
    });
    return () => {
      audio.pause();
      audio.currentTime = 0;
    };
  }, []);

  useEffect(() => {
    setLocalIndex(remoteIndex);
  }, [remoteIndex]);

  // Drive the reveal timer. Writes new index to Firestore as source of truth.
  useEffect(() => {
    if (!gameId || superlatives.length === 0) return;
    if (localIndex >= superlatives.length) return;

    tickerRef.current = setTimeout(() => {
      const outgoing = superlatives[localIndex];
      const nextIndex = localIndex + 1;

      if (nextIndex >= superlatives.length) {
        // Last card — animate it out, then mark the reveal complete.
        setOutgoingSuperlative(outgoing);
        Provider.markSuperlativesFinale(gameId);
      } else {
        // Batched: outgoing + advance in the same render cycle so the incoming
        // slot immediately shows the next superlative while the outgoing slot
        // animates the previous one off-screen.
        setOutgoingSuperlative(outgoing);
        setLocalIndex(nextIndex);
        Provider.updateSuperlativeIndex(gameId, nextIndex);
      }

      outgoingCleanupRef.current = setTimeout(() => {
        setOutgoingSuperlative(null);
      }, EXIT_MS);
    }, HOLD_MS);

    return () => {
      if (tickerRef.current) clearTimeout(tickerRef.current);
      if (outgoingCleanupRef.current) clearTimeout(outgoingCleanupRef.current);
    };
  }, [gameId, localIndex, superlatives]);

  if (superlatives.length === 0) return null;

  const current = superlatives[localIndex] || null;

  return (
    <Root>
      <LeaderboardOverlay>
        <GameboardLeaderboard />
      </LeaderboardOverlay>
      <RevealCol>
        <CardStack>
          {outgoingSuperlative && (
            <SpotlightCard
              key={`out-${outgoingSuperlative.id}`}
              superlative={outgoingSuperlative}
              role="outgoing"
            />
          )}
          {current && (
            <SpotlightCard
              key={`in-${current.id}`}
              superlative={current}
              role="incoming"
            />
          )}
        </CardStack>
        <Box sx={{ marginTop: 4 }}>
          <ProgressDots
            total={superlatives.length}
            currentIndex={localIndex}
            holdMs={HOLD_MS}
          />
        </Box>
      </RevealCol>
    </Root>
  );
};

export default BigScreenReveal;
